<script setup lang="ts">
import { computed, provide, useTemplateRef } from "vue";

import ActionMenu from "@/components/ActionMenu/ActionMenu.vue";
import ActionSource from "@/components/action/ActionSource.vue";
import { boardKey } from "@/components/board/injectKeys";
import { useBoardStore } from "@/store/board";
import { useSelectionStore } from "@/store/selection";

import SelectionActionMenu from "./internal/SelectionActionMenu.vue";
import { useMultiSelectRect } from "./internal/useMultiSelectRect";
import { useSelectionActionMenu } from "./internal/useSelectionActionMenu";
import { useStickyBoundingRect } from "./internal/useStickyBoundingRect";

const width = 1600;
const height = 900;
const boundingRectRef = useTemplateRef("boundingRectRef");

const { boundingRect } = useStickyBoundingRect(width, height);
const { multiSelectRect } = useMultiSelectRect(width, height);
const isNotSelecting = computed(() => !useSelectionStore().isSelectingWithRect);
const selectionMenu = useSelectionActionMenu(boundingRectRef, isNotSelecting, boundingRect);

const board = computed(() => useBoardStore().currentBoard());
provide(boardKey, board);

const rectStrokeWidth = computed(() => {
  const baseStrokeWidth = 0.25;

  const cardSize = useBoardStore().currentBoard().cardSize;
  return cardSize.factor * baseStrokeWidth;
});

const zIndexRectangleSelection = computed(() => board.value.maxZ + 1);
</script>

<template>
  <ActionMenu :controller="selectionMenu">
    <ActionSource value="selection-actions-menu">
      <SelectionActionMenu />
    </ActionSource>
  </ActionMenu>
  <svg
    class="selection-layer"
    :viewBox="`0 0 ${width} ${height}`"
    stroke="currentColor"
    :style="{
      zIndex: zIndexRectangleSelection,
      pointerEvents: 'none',
      strokeWidth: rectStrokeWidth,
    }"
    aria-hidden="true"
  >
    <rect v-if="!!boundingRect" ref="boundingRectRef" v-bind="boundingRect" class="bounding-rect" />
    <rect v-if="!!multiSelectRect" v-bind="multiSelectRect" class="multi-select-rect-fill" />
    <rect v-if="!!multiSelectRect" v-bind="multiSelectRect" class="multi-select-rect-outline" />
  </svg>
</template>

<style lang="scss">
@use "@/styles/variables";
@use "@/styles/variables/colors";
@use "@/styles/variables/a11y" as colors-a11y;

.selection-layer {
  position: absolute;
  width: 100% * variables.$fake-zoom;
  height: 100% * variables.$fake-zoom;
  fill: none;

  .bounding-rect {
    color: colors.$main;
  }

  .multi-select-rect-fill {
    fill: colors.$main;
    opacity: 0.1;
  }

  .multi-select-rect-outline {
    fill: none;
    stroke: colors.$main;
  }
}
</style>
